'use client';

import React from 'react';
import styles from './ServiceContent.module.scss';

interface ServiceFeature {
  icon: string;
  title: string;
  description: string;
  details?: string[];
}

interface ServiceSection {
  title: string;
  description: string;
  features: ServiceFeature[];
}

interface ServiceTechStack {
  category: string;
  items: {
    name: string;
    description: string;
    details?: string;
  }[];
}

interface ServiceContentProps {
  title: string;
  description: string;
  sections: ServiceSection[];
  techStack?: ServiceTechStack[];
  achievements?: string[];
  demoLink?: {
    text: string;
    url: string;
    description: string;
  };
  externalLink?: {
    text: string;
    url: string;
    description: string;
  };
}

export function ServiceContent({
  title,
  description,
  sections,
  techStack,
  achievements,
  demoLink,
  externalLink
}: ServiceContentProps) {
  return (
    <div className={styles.serviceContent}>
      {/* Main Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.description}>{description}</p>
      </div>

      {/* Sections */}
      {sections.map((section, sectionIndex) => (
        <section key={sectionIndex} className={styles.section}>
          <h3 className={styles.sectionTitle}>{section.title}</h3>
          <p className={styles.sectionDescription}>{section.description}</p>
          
          <div className={styles.featuresGrid}>
            {section.features.map((feature, featureIndex) => (
              <div key={featureIndex} className={styles.featureCard}>
                <div className={styles.featureIcon}>{feature.icon}</div>
                <h4 className={styles.featureTitle}>{feature.title}</h4>
                <p className={styles.featureDescription}>{feature.description}</p>
                
                {feature.details && (
                  <ul className={styles.featureDetails}>
                    {feature.details.map((detail, detailIndex) => (
                      <li key={detailIndex}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* Tech Stack */}
      {techStack && techStack.length > 0 && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>採用技術スタック</h3>
          
          {techStack.map((stack, stackIndex) => (
            <div key={stackIndex} className={styles.techCategory}>
              <h4 className={styles.techCategoryTitle}>{stack.category}</h4>
              <div className={styles.techItems}>
                {stack.items.map((item, itemIndex) => (
                  <div key={itemIndex} className={styles.techItem}>
                    <div className={styles.techName}>{item.name}</div>
                    <div className={styles.techDescription}>{item.description}</div>
                    {item.details && (
                      <div className={styles.techDetails}>{item.details}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Achievements */}
      {achievements && achievements.length > 0 && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>実績・導入事例</h3>
          <div className={styles.achievementsList}>
            {achievements.map((achievement, index) => (
              <div key={index} className={styles.achievementItem}>
                <div className={styles.achievementIcon}>✓</div>
                <div className={styles.achievementText}>{achievement}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Demo & External Links */}
      {(demoLink || externalLink) && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>体験・詳細情報</h3>
          <div className={styles.linksGrid}>
            {demoLink && (
              <div className={styles.linkCard}>
                <div className={styles.linkIcon}>🎯</div>
                <div className={styles.linkContent}>
                  <h4 className={styles.linkTitle}>実際に体験</h4>
                  <p className={styles.linkDescription}>{demoLink.description}</p>
                  <a 
                    href={demoLink.url} 
                    className={styles.linkButton}
                    target={demoLink.url.startsWith('http') ? '_blank' : '_self'}
                    rel={demoLink.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {demoLink.text}
                  </a>
                </div>
              </div>
            )}
            
            {externalLink && (
              <div className={styles.linkCard}>
                <div className={styles.linkIcon}>🔗</div>
                <div className={styles.linkContent}>
                  <h4 className={styles.linkTitle}>詳細情報</h4>
                  <p className={styles.linkDescription}>{externalLink.description}</p>
                  <a 
                    href={externalLink.url} 
                    className={styles.linkButton}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {externalLink.text}
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default ServiceContent;